import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { IndividualsAPI } from "../../api/individuals";
import { NEW_CASE_PATH } from "../../features/caseIde/routes";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  clientName?: string;
  individualData?: {
    id: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    email: string;
    visa: string;
    employerInfo?: {
      legalBusinessName?: string;
    };
    employerPetitioner?: string;
    petitionerName?: string;
    title?: string;
    pronouns?: string;
  };
};

/** The client a case belongs to, or null for unlinked legacy cases. */
const clientIdForCase = async (
  individualId: string
): Promise<string | null> => {
  try {
    const individual = (await IndividualsAPI.getIndividual(
      individualId
    )) as Record<string, unknown>;
    const id = individual.foreign_national_id;
    return typeof id === "string" && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
};

export const AddWorkflowModal = ({
  isOpen,
  onClose,
  clientName,
  individualData
}: Props) => {
  const navigate = useNavigate();
  const [isResolving, setIsResolving] = useState(false);

  const handleContinue = async () => {
    setIsResolving(true);
    const clientId = individualData
      ? await clientIdForCase(individualData.id)
      : null;
    setIsResolving(false);
    onClose();
    navigate(
      clientId
        ? `${NEW_CASE_PATH}?clientId=${encodeURIComponent(clientId)}`
        : NEW_CASE_PATH
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Workflow</DialogTitle>
          <DialogDescription>
            This will add a new process for {clientName || "this client"}'s
            case. Any previously uploaded documents will automatically transfer.
          </DialogDescription>
        </DialogHeader>
        <p className="text-base text-fg-2">Would you like to continue?</p>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={isResolving}
          >
            {isResolving ? <Spinner /> : null}
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
